import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';

import CustomButton from '../../../../../../../../lib/designSystem/Button';
import { formatMoney } from '../../../../../../../../lib/functions/utils';

interface GroupedLine {
  account: string;
  accountName: string;
  description: string;
  documentId: string;
  debit: number;
  credit: number;
  auxiliaryBusinessIdentifier: string;
}

interface Entry {
  businessIdentifier: string;
  businessName: string;
  category: string;
  groupedLines: GroupedLine[];
  date: string;
  sourceKey?: string;
  isEdited?: boolean;
}

interface EntryModalProps {
  entry?: Entry;
  open: boolean;
  onClose: () => void;
  onEdit?: (entry: Entry) => void;
  onSave?: (entry: Entry) => void;
  saving?: boolean;
  readOnly?: boolean;
  allowSaving?: boolean;
}

const EntryModal: React.FC<EntryModalProps> = ({
  entry,
  open,
  onClose,
  onEdit,
  onSave,
  saving = false,
  readOnly = false,
  allowSaving = false,
}) => {
  // Check if any line has auxiliaryBusinessIdentifier
  const hasAuxiliaryData = (entry?.groupedLines ?? []).some(
    (line: GroupedLine) => line.auxiliaryBusinessIdentifier,
  );
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={8}>
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 600,
                color: '#2c3e50',
                mb: 1,
              }}
            >
              {entry?.category}
            </Typography>
            <Typography
              variant="body1"
              component="div"
              sx={{
                color: '#7f8c8d',
                fontWeight: 500,
              }}
            >
              {entry?.businessName}
            </Typography>
            <Typography
              variant="body2"
              component="div"
              sx={{
                color: '#95a5a6',
                fontStyle: 'italic',
              }}
            >
              RUT: {entry?.businessIdentifier}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                textAlign: { xs: 'left', md: 'right' },
                color: '#2c3e50',
                fontWeight: 500,
              }}
            >
              {entry?.date}
            </Typography>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cuenta</TableCell>
              <TableCell>Nombre de Cuenta</TableCell>
              <TableCell>Descripción</TableCell>
              {hasAuxiliaryData && <TableCell>Auxiliar</TableCell>}
              <TableCell>Documento</TableCell>
              <TableCell>Debe</TableCell>
              <TableCell>Haber</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(entry?.groupedLines ?? []).map(
              (line: GroupedLine, lineIndex: number) => (
                <TableRow key={lineIndex}>
                  <TableCell>{line.account}</TableCell>
                  <TableCell>{line.accountName}</TableCell>
                  <TableCell>{line.description}</TableCell>
                  {hasAuxiliaryData && (
                    <TableCell
                      style={{
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {line.auxiliaryBusinessIdentifier || '-'}
                    </TableCell>
                  )}
                  <TableCell>{line.documentId}</TableCell>
                  <TableCell>{formatMoney(line.debit)}</TableCell>
                  <TableCell>{formatMoney(line.credit)}</TableCell>
                </TableRow>
              ),
            )}
            <TableRow>
              <TableCell
                colSpan={hasAuxiliaryData ? 5 : 4}
                style={{
                  fontWeight: 'bold',
                  textAlign: 'left',
                }}
              >
                Totales:
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  (entry?.groupedLines ?? []).reduce(
                    (sum: number, line: GroupedLine) => sum + line.debit,
                    0,
                  ),
                )}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  (entry?.groupedLines ?? []).reduce(
                    (sum: number, line: GroupedLine) => sum + line.credit,
                    0,
                  ),
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        {!readOnly && (
          <CustomButton
            color="primary"
            onClick={() => {
              if (entry && onEdit) {
                onEdit(entry);
              }
              onClose();
            }}
          >
            Editar
          </CustomButton>
        )}
        {allowSaving && (
          <CustomButton
            color="success"
            disabled={saving}
            onClick={() => {
              if (entry && onSave) {
                onSave(entry);
              }
            }}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </CustomButton>
        )}
        <CustomButton onClick={onClose} color="secondary">
          Cerrar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default EntryModal;
